import InfoRounded from '@mui/icons-material/InfoRounded';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Button } from '@openx/components-core/lib/Button/Button';
import type {
  AudienceSegmentExcelGenerateFiltersInput,
  AudienceSegmentExcelGenerateInputSegment,
} from '@openx/types/gqlTypes';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DealPromptTileDetailsPayloadForAudience } from '../../../constants';
import { useAudienceExcelGenerate } from '../../../hooks/useAudienceExeclGenerate';
import { useDealPrompt } from '../../../hooks/useDealPrompt';
import { checkExportEligibility } from '../../../utils/checkAudienceExportEligibility';
import { useDealPromptDomainsContext } from '../../../utils/context/DealPromptDomainsContext';
import { downloadAsXls } from '../../../utils/downloadAsXls';
import { mapAudienceCriteriaToFiltersBody } from '../../../utils/filters/filterMappers';
import { dealPromptContextTypes } from '../../../utils/types';
import { ExportRulesDrawer } from './ExportRulesDrawer';
import { combineTagsIntoName } from './utils';

export const DealPromptDetailsExportButton = () => {
  const { t } = useTranslation();
  const fetchDealPromptAudienceExcelGenerate = useAudienceExcelGenerate();

  const [isDrawerOpen, setDrawerOpen] = useState<boolean>(false);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const {
    state: { contextType, selectedAudienceItems, selectedTile },
  } = useDealPrompt();
  const { generatedSearchInput, useDealPromptFilters } = useDealPromptDomainsContext();
  const { criteria } = useDealPromptFilters();

  const isExportDisabled = useMemo(() => {
    if (contextType === dealPromptContextTypes.AUDIENCE) {
      return !checkExportEligibility(selectedAudienceItems);
    }
    return false;
  }, [contextType, selectedAudienceItems]);

  const exportAudienceSegments = useCallback(async () => {
    const mappedSegments: AudienceSegmentExcelGenerateInputSegment[] = selectedAudienceItems.map(item => ({
      account_id: item.account_id,
      account_name: item.account_name,
      category: item.category,
      countries: item.countries,
      description: item.description ?? '',
      name: item.name ?? '',
      regions: item.regions,
      segment_category: item.segment_category ?? '',
      segment_subcategory: item.segment_subcategory ?? '',
      segment_type: item.segment_type ?? '',
      sellable: item.sellable,
    }));

    const searchFilters = mapAudienceCriteriaToFiltersBody(criteria);
    const filters: AudienceSegmentExcelGenerateFiltersInput = {
      demographicsAge: searchFilters.demographicsAge && {
        min: searchFilters.demographicsAge.min,
        max: searchFilters.demographicsAge.max,
      },
      demographicsGender: searchFilters.demographicsGender,
      userLocationDMAs: searchFilters.userLocationDMAs?.length ? [...searchFilters.userLocationDMAs] : undefined,
      userLocationStates: searchFilters.userLocationStates?.length ? [...searchFilters.userLocationStates] : undefined,
    };

    try {
      const result = await fetchDealPromptAudienceExcelGenerate({
        segmentData: {
          filters,
          mau: (selectedTile as DealPromptTileDetailsPayloadForAudience)?.item?.metrics?.reach ?? 0,
          segments: mappedSegments,
        },
      });
      downloadAsXls(
        result.fileData,
        `audience_segments_${combineTagsIntoName(generatedSearchInput?.tagGroups.groups ?? [])}.xlsx`
      );
    } catch (_error) {
      enqueueSnackbar(t('Failed to export audience segments'), { variant: 'error' });
    }
  }, [fetchDealPromptAudienceExcelGenerate, selectedAudienceItems, selectedTile, t, generatedSearchInput, criteria]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {isExportDisabled ? (
        <Tooltip title={t('Export is unavailable for this selection. Some segments cannot be exported.')}>
          <span>
            <Button
              color="primary"
              data-test="export-audience-segments"
              data-pendo-feature={`deal-prompt-details-${contextType}-export`}
              disabled={isExportDisabled}
              onClick={() => exportAudienceSegments()}
            >
              {t('Export')}
            </Button>
          </span>
        </Tooltip>
      ) : (
        <Button
          color="primary"
          data-test="export-audience-segments"
          data-pendo-feature={`deal-prompt-details-${contextType}-export`}
          disabled={isExportDisabled}
          onClick={() => exportAudienceSegments()}
        >
          {t('Export')}
        </Button>
      )}

      <IconButton onClick={() => setDrawerOpen(true)}>
        <InfoRounded />
      </IconButton>

      <ExportRulesDrawer onClose={closeDrawer} open={isDrawerOpen} />
    </Box>
  );
};
